import { FC, ReactNode, useMemo, useState } from 'react'
import { IPageParams } from '../../api/IApi'
import { ticketApi } from '../../api/ticket.api'
import { useTicket } from '../../hooks/useTicket'
import { useTicketTableCols } from '../../hooks/useTicketTableCols'
import { useTypedDispatch } from '../../hooks/useTypedDispatch'
import { useTypedSelector } from '../../hooks/useTypedSelector'
import {
  getTicketPageParams,
  getTicketPageParamsMarked,
  setTicketsPageParams,
  setTicketsPageParamsMarked,
} from '../../store/ticketSlice'
import parseISOToString from '../../utils/parseISOToString'
import Table from '../common/table/Table'
import Checkbox from '../form/Checkbox'
import TicketPriority from './TicketPriority'
import TicketStatus from './TicketStatus'
import { ITicket } from '../../model/ITicket'
import { CONFIG } from '../../config'
import SlideDown from '../common/slideDown/SlideDown'
import Button from '../form/Button'
import { icons } from '../../icons/IconSet'

const getClassNameTrByWithoutResponse = (
  days: ITicket['businessDaysWithoutResponse'] = 0,
): string =>
  (CONFIG.getDeadlineTicketType(days) === 'error' &&
    'bg-red-200 hover:bg-red-300 [&>td]:text-red-900') ||
  (CONFIG.getDeadlineTicketType(days) === 'warning' &&
    'bg-orange-100 hover:bg-orange-200 [&>td]:text-orange-900') ||
  ''

const TicketTable: FC = () => {
  const dispatch = useTypedDispatch()

  const { addTicketById, getTicketsFilterOptions } = useTicket()
  const { theadCols, allTablesCols } = useTicketTableCols()

  const [showExpiredTickets, setShowExpiredTickets] = useState<boolean>(false)

  // --
  // -- Common tickets list
  const ticketsPageParams = useTypedSelector(getTicketPageParams())
  const { data: tickets, isFetching } = ticketApi.useGetTicketsQuery({
    ...ticketsPageParams,
    ...getTicketsFilterOptions(),
  })

  // --
  // -- Marked tickets list
  const ticketsPageParamsMarked = useTypedSelector(getTicketPageParamsMarked())
  const {
    data: ticketsMarked,
    isFetching: isFetchingMarked,
    isSuccess: isTicketMarkedSuccess,
  } = ticketApi.useGetTicketsQuery({
    ...ticketsPageParamsMarked,
    ...getTicketsFilterOptions(),
    isTicketUnasnwered: true,
  })

  const ticketMarkedNumber: number | undefined = useMemo(() => {
    if (!isTicketMarkedSuccess) return undefined

    return (
      (ticketsMarked?.content || [])?.filter(
        ({ businessDaysWithoutResponse }) =>
          CONFIG.getDeadlineTicketType(businessDaysWithoutResponse) === 'error',
      )?.length || undefined
    )
  }, [isTicketMarkedSuccess])

  return !theadCols ? null : (
    <>
      {!!ticketMarkedNumber && (
        <SlideDown
          isShowDefault={showExpiredTickets}
          isShowToggle={(isShow: boolean) => setShowExpiredTickets(() => isShow)}
          Button={<Button
            isNotFullWidth
            variant='outline'
            size='tiny'
            type='delete'
            className='m-2 w-[16em] flex gap-1 justify-center items-center sticky top-[-4px] left-[-10px] z-10'
          >
            {showExpiredTickets ? 'Hide' : 'Show'} Expired Tickets {ticketMarkedNumber}
            {ticketMarkedNumber >= (ticketsPageParamsMarked?.size || 0) ? '+' : ''}
            {showExpiredTickets
              ? icons('chevron-up', { width: '1.4em' })
              : icons('chevron-down', { width: '1.2em' })}
          </Button>}
        >
          <Table
            size='small'
            isPending={isFetchingMarked}
            pageParams={ticketsPageParamsMarked}
            pagable={ticketsMarked}
            updatePageParams={(newParams: Partial<IPageParams>) => dispatch(
              setTicketsPageParamsMarked({
                ...ticketsPageParamsMarked,
                ...newParams,
              })
            )}
            thead={theadCols.map((colKey) => allTablesCols[colKey] || { title: '' })}
            tbody={ticketsMarked && ticketsMarked?.content?.length
              ? ticketsMarked.content
                .filter(
                  ({ businessDaysWithoutResponse }) => CONFIG.getDeadlineTicketType(businessDaysWithoutResponse) === 'error'
                )
                .map((ticket) => {
                  const allRowData: Record<keyof typeof allTablesCols, ReactNode> = {
                    '': <Checkbox name='ticket' value={false} />,
                    'Ticket #': ticket.id,
                    Priority: <TicketPriority variant='error' status={ticket.priority.name} />,
                    Status: <TicketStatus variant='error' statusId={ticket.status.id} />,
                    Type: ticket?.type?.name || '-',
                    Customer: ticket?.client?.name || '-',
                    'Order #': ticket?.orderId || '-',
                    // 'Vendor Action': ticket?.lastVendorAction?.actionText || '-',
                    'Vendor Name': ticket?.vendor?.name || '-',
                    'Vendor Order #': ticket?.supplierOrderId || '-',
                    'Last Updated By': ticket?.lastChangedBy?.name || 'Customer',
                    'Action Required By': ticket.actionRequiredBy || '-',
                    Created: parseISOToString(ticket.created),
                    'Last Updated': (ticket?.updated && parseISOToString(ticket.updated)) || '-',
                    'Assigned Vendor Group': ticket?.assignedVendorGroup?.name || '-',
                    'Assigned Agent Group': ticket?.assignedAgentGroup?.name || '-',
                  }

                  return {
                    onClick: () => addTicketById(ticket.id),
                    data: theadCols.map((colKey) => allRowData[colKey]),
                    className: getClassNameTrByWithoutResponse(
                      ticket.businessDaysWithoutResponse
                    ),
                  }
                })
              : []} />
        </SlideDown>
      )}

      <Table
        size='small'
        isPending={isFetching}
        pageParams={ticketsPageParams}
        pagable={tickets}
        updatePageParams={(newParams: Partial<IPageParams>) =>
          dispatch(
            setTicketsPageParams({
              ...ticketsPageParams,
              ...newParams,
            }),
          )
        }
        thead={theadCols.map((colKey) => allTablesCols[colKey] || { title: '' })}
        tbody={
          tickets && tickets?.content?.length
            ? tickets.content.map((ticket) => {
              const variant = CONFIG.getDeadlineTicketType(ticket?.businessDaysWithoutResponse)

              const allRowData: Record<keyof typeof allTablesCols, ReactNode> = {
                '': <Checkbox name='ticket' value={false} />,
                'Ticket #': ticket.id,
                Priority: <TicketPriority variant={variant} status={ticket.priority.name} />,
                Status: <TicketStatus variant={variant} statusId={ticket.status.id} />,
                Type: ticket?.type?.name || '-',
                Customer: ticket?.client?.name || '-',
                'Order #': ticket?.orderId || '-',
                // 'Vendor Action': ticket?.lastVendorAction?.actionText || '-',
                'Vendor Name': ticket?.vendor?.name || '-',
                'Vendor Order #': ticket?.supplierOrderId || '-',
                'Last Updated By': ticket?.lastChangedBy?.name || 'Customer',
                'Action Required By': ticket.actionRequiredBy || '-',
                Created: parseISOToString(ticket.created),
                'Last Updated': (ticket?.updated && parseISOToString(ticket.updated)) || '-',
                'Assigned Vendor Group': ticket?.assignedVendorGroup?.name || '-',
                'Assigned Agent Group': ticket?.assignedAgentGroup?.name || '-',
              }

              return {
                onClick: () => addTicketById(ticket.id),
                data: theadCols.map((colKey) => allRowData[colKey]),
                className: getClassNameTrByWithoutResponse(ticket.businessDaysWithoutResponse),
              }
            })
            : []
        }
      />
    </>
  )
}

export default TicketTable
